import { Checkbox } from '@1trading/react/components/ui/checkbox';
import type { ComponentProps } from 'react';
import type { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';

import { NONE_VALUE, type NoneAnswer } from '@/constants/answers';
import { remove } from '@/utils/array';

type CheckboxProps = Omit<ComponentProps<typeof Checkbox>, 'checked' | 'onCheckedChange'>;

const isNoneAnswer = <TFieldValues extends FieldValues>(value: TFieldValues[] | NoneAnswer | undefined, noneValue: string): value is NoneAnswer =>
  typeof value !== 'undefined' && value[0] === noneValue;

const isNonNoneAnswer = <TFieldValues extends FieldValues>(value: TFieldValues[] | NoneAnswer | undefined, noneValue: string): value is TFieldValues[] =>
  typeof value !== 'undefined' && value[0] !== noneValue;

type NoneCheckboxGroupNoneCheckboxProps<TFieldValues extends FieldValues> = { field: ControllerRenderProps<TFieldValues>; noneValue?: string } & CheckboxProps;

const NoneCheckboxGroupNoneCheckbox = <TFieldValues extends FieldValues>({ field, noneValue = NONE_VALUE, ...props }: NoneCheckboxGroupNoneCheckboxProps<TFieldValues>) => {
  return (
    <Checkbox checked={isNoneAnswer(field.value, noneValue)} onCheckedChange={(checked) => field.onChange(checked ? [noneValue] : remove(field.value, noneValue))} {...props} />
  );
};
NoneCheckboxGroupNoneCheckbox.displayName = 'NoneCheckboxGroupNoneCheckbox';

type NoneCheckboxGroupNonNoneCheckboxProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  answer: TFieldValues[TName] extends readonly (infer T)[] | undefined ? T : never;
  field: ControllerRenderProps<TFieldValues, TName>;
  noneValue?: string;
} & CheckboxProps;

const NoneCheckboxGroupNonNoneCheckbox = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  answer,
  field,
  noneValue = NONE_VALUE,
  ...props
}: NoneCheckboxGroupNonNoneCheckboxProps<TFieldValues, TName>) => {
  const value = field.value ?? ([] as TFieldValues[TName]);

  return (
    <Checkbox
      checked={isNonNoneAnswer(value, noneValue) ? value.includes(answer) : false}
      onCheckedChange={(checked) => field.onChange(checked ? [...remove(value, noneValue), answer] : remove(value, answer))}
      {...props}
    />
  );
};
NoneCheckboxGroupNonNoneCheckbox.displayName = 'NoneCheckboxGroupNonNoneCheckbox';

export { NoneCheckboxGroupNoneCheckbox, NoneCheckboxGroupNonNoneCheckbox };
